/**
 * The Journeys report's arithmetic, kept apart from the page so it can be tested without one.
 *
 * Everything here is pure: the filters as a query and as an address, the replay's clock and
 * the folds in it, where the car was at any moment, which chapter that moment belongs to,
 * where the skip buttons land, and which key does what. The page, the player and the map own
 * the state; this file owns the rules.
 */

import type { Schema } from '@taxi/api-client';

import { declineWords, isCode, riderCancellationWords, type Translate } from '../orders/history';
// From its own file, not the barrel: the client tests compile this without the DOM, and the
// barrel brings every primitive that touches it.
import type { Tone } from '../ui/Pill';

export type JourneyList = Schema<'JourneyList'>;

export type JourneyRow = Schema<'JourneyRow'>;

export type JourneyDetail = Schema<'JourneyDetail'>;

export type JourneyDriverLeg = Schema<'JourneyDriverLeg'>;

export type JourneyPoint = Schema<'JourneyPoint'>;

export type JourneySpan = Schema<'JourneySpan'>;

export type JourneyEvent = Schema<'JourneyEvent'>;

export type JourneyCall = Schema<'JourneyCall'>;

export type JourneyMeasures = Schema<'JourneyMeasures'>;

export type FareLine = Schema<'FareLine'>;

// --- Filters -------------------------------------------------------------------

/** What the report can call a problem, in the order the chips show them. */
export const PROBLEMS = [
  'late',
  'detour',
  'longWait',
  'longStop',
  'speeding',
  'cancelledOnTheWay',
  'signalLost',
  'noTrack',
] as const;

export type Problem = (typeof PROBLEMS)[number];

export type StateFilter = 'all' | 'Completed' | 'Cancelled' | 'NoShow';

export type SourceFilter = 'all' | 'phone' | 'app' | 'street';

/**
 * How an order arrived, as one of the three words the journeys pages use: the desk's
 * telephone, the passenger's app, or the driver himself taking a passenger off the street
 * (#407). The web and an integration are the app's, as they always were here.
 */
export function sourceWord(source: string): 'phone' | 'app' | 'street' {
  if (source === 'Phone') return 'phone';
  if (source === 'Street') return 'street';
  return 'app';
}

export interface JourneyFilters {
  /** A reference, a number typed any way, or part of a name, an address or a plate. */
  text: string;
  state: StateFilter;
  source: SourceFilter;
  driver: string | null;
  vehicle: string | null;
  /** Set only by a link from the clients report; there is no control for it on the page. */
  customer: string | null;
  problems: Problem[];
}

export const NO_FILTERS: JourneyFilters = {
  text: '',
  state: 'all',
  source: 'all',
  driver: null,
  vehicle: null,
  customer: null,
  problems: [],
};

/** Whether anything narrows the list beyond the period. */
export function isFiltered(filters: JourneyFilters): boolean {
  return (
    filters.text.trim() !== '' ||
    filters.state !== 'all' ||
    filters.source !== 'all' ||
    filters.driver !== null ||
    filters.vehicle !== null ||
    filters.customer !== null ||
    filters.problems.length > 0
  );
}

/** The filters as name–value pairs, defaults left out, in a fixed order. */
function pairs(filters: JourneyFilters): [string, string][] {
  const out: [string, string][] = [];
  const text = filters.text.trim();

  if (text !== '') out.push(['q', text]);
  if (filters.state !== 'all') out.push(['state', filters.state]);
  if (filters.source !== 'all') out.push(['source', filters.source]);
  if (filters.driver !== null) out.push(['driver', filters.driver]);
  if (filters.vehicle !== null) out.push(['vehicle', filters.vehicle]);
  if (filters.customer !== null) out.push(['customer', filters.customer]);
  if (filters.problems.length > 0) out.push(['problems', filters.problems.join(',')]);

  return out;
}

/**
 * The filters as query parameters, to follow the period's.
 *
 * Every value is encoded, because the search box is free text — a `+` from an international
 * number would otherwise arrive as a space and match nobody.
 *
 * @param page From nought. The first page is left out like any other default.
 */
export function filterQuery(filters: JourneyFilters, page = 0): string {
  const parts = pairs(filters).map(([name, value]) => `&${name}=${encodeURIComponent(value)}`);

  if (page > 0) parts.push(`&page=${page}`);

  return parts.join('');
}

/** The filters as they sit in the page's address, so a filtered list is a link. */
export function filterParams(filters: JourneyFilters): Record<string, string> {
  return Object.fromEntries(pairs(filters));
}

/**
 * The filters an address asks for. Anything it does not recognise is left at its default
 * rather than guessed at — a link that half-applies is worse than one that says nothing.
 */
export function filtersFrom(params: URLSearchParams): JourneyFilters {
  const state = params.get('state');
  const source = params.get('source');

  return {
    text: params.get('q') ?? '',
    state: state === 'Completed' || state === 'Cancelled' || state === 'NoShow' ? state : 'all',
    source: source === 'phone' || source === 'app' || source === 'street' ? source : 'all',
    driver: params.get('driver') || null,
    vehicle: params.get('vehicle') || null,
    customer: params.get('customer') || null,
    problems: (params.get('problems') ?? '')
      .split(',')
      .filter((name): name is Problem => (PROBLEMS as readonly string[]).includes(name)),
  };
}

/** A chip toggled in or out, keeping the chips' own order. */
export function toggleProblem(problems: readonly Problem[], problem: Problem): Problem[] {
  return problems.includes(problem)
    ? problems.filter((p) => p !== problem)
    : PROBLEMS.filter((p) => p === problem || problems.includes(p));
}

/** How loud a problem is. Red for what a client complains about; amber for what to look at. */
export function problemTone(problem: string): Tone {
  switch (problem) {
    case 'late':
    case 'detour':
    case 'speeding':
      return 'danger';
    case 'noTrack':
    case 'signalLost':
      return 'neutral';
    default:
      return 'warn';
  }
}

// --- The replay's clock --------------------------------------------------------

/**
 * A quiet stretch longer than this, outside the drive itself, is folded away.
 *
 * A pre-booking taken on Monday for Wednesday has two days between its first two events, and
 * a scrubber that spent ninety-nine per cent of its width on nothing would leave the ride
 * itself a few pixels wide.
 */
export const FOLD_AFTER_MS = 5 * 60_000;

/** How much of the axis a folded stretch keeps, so the fold is still visible as a mark. */
export const FOLDED_MS = 20_000;

/** A stretch of real time that the axis squeezes into {@link FOLDED_MS}. */
export interface Fold {
  from: number;
  to: number;
}

/**
 * The replay's axis: real time from the first thing that happened to the last, with the
 * quiet stretches folded. Positions on the scrubber and the playhead are axis milliseconds;
 * {@link toAxis} and {@link fromAxis} convert.
 */
export interface Axis {
  /** Real milliseconds at the start. */
  start: number;
  /** Real milliseconds at the end. */
  end: number;
  folds: Fold[];
  /** The axis's own length, in milliseconds, folds squeezed. */
  length: number;
}

const DRIVING = new Set(['approach', 'waiting', 'aboard']);

/** The parts a distance belongs to. */
const MOVING = new Set(['approach', 'aboard']);

/** Every moment something happened, in real milliseconds, sorted. */
function moments(journey: JourneyDetail): number[] {
  const at: number[] = [Date.parse(journey.requestedAt)];

  if (journey.finishedAt) at.push(Date.parse(journey.finishedAt));

  for (const chapter of journey.chapters) at.push(Date.parse(chapter.from), Date.parse(chapter.to));
  for (const event of journey.events) at.push(Date.parse(event.at));

  for (const call of journey.calls) {
    at.push(Date.parse(call.startedAt));
    if (call.endedAt) at.push(Date.parse(call.endedAt));
  }

  for (const leg of journey.legs) for (const point of leg.points) at.push(Date.parse(point.at));

  return at.filter(Number.isFinite).sort((a, b) => a - b);
}

/**
 * The axis for a journey.
 *
 * The rating is left off the end: a passenger who rates the next morning would otherwise
 * stretch every replay by a night. It stays in the list of what happened.
 */
export function axisOf(journey: JourneyDetail): Axis {
  const rated = new Set(
    journey.events.filter((e) => e.kind === 'rated').map((e) => Date.parse(e.at)),
  );

  const at = moments(journey).filter(
    (moment) =>
      !rated.has(moment) || moment <= Date.parse(journey.finishedAt ?? journey.requestedAt),
  );

  const start = at[0] ?? Date.parse(journey.requestedAt);
  const end = Math.max(at[at.length - 1] ?? start, start + 1000);

  const driving = journey.chapters
    .filter((chapter) => DRIVING.has(chapter.kind))
    .map((chapter) => ({ from: Date.parse(chapter.from), to: Date.parse(chapter.to) }));

  const folds: Fold[] = [];

  for (let i = 1; i < at.length; i++) {
    const from = at[i - 1];
    const to = at[i];

    if (to - from <= FOLD_AFTER_MS) continue;

    // A silence inside the drive is the drive: the replay plays it, dashed, rather than
    // hiding the minutes a phone said nothing.
    if (driving.some((span) => from < span.to && to > span.from)) continue;

    folds.push({ from, to });
  }

  const squeezed = folds.reduce((sum, fold) => sum + (fold.to - fold.from - FOLDED_MS), 0);

  return { start, end, folds, length: end - start - squeezed };
}

/** Real milliseconds to a position on the axis. */
export function toAxis(axis: Axis, real: number): number {
  const clamped = Math.min(Math.max(real, axis.start), axis.end);
  let position = clamped - axis.start;

  for (const fold of axis.folds) {
    if (clamped <= fold.from) break;

    const inside = Math.min(clamped, fold.to) - fold.from;
    const length = fold.to - fold.from;

    // Inside a fold the position moves through its squeezed width proportionally.
    position -= inside - (inside / length) * FOLDED_MS;
  }

  return position;
}

/** A position on the axis back to real milliseconds. */
export function fromAxis(axis: Axis, position: number): number {
  let remaining = Math.min(Math.max(position, 0), axis.length);
  let real = axis.start;

  for (const fold of axis.folds) {
    const before = fold.from - real;

    if (remaining <= before) return real + remaining;

    remaining -= before;
    real = fold.from;

    if (remaining <= FOLDED_MS) return real + (remaining / FOLDED_MS) * (fold.to - fold.from);

    remaining -= FOLDED_MS;
    real = fold.to;
  }

  return Math.min(real + remaining, axis.end);
}

// --- Where the car was ---------------------------------------------------------

/** Where the car was at a moment, interpolated between the readings either side. */
export interface CarAt {
  longitude: number;
  latitude: number;
  /** Degrees from north; null when it stood still and nothing said which way it faced. */
  heading: number | null;
  speedKmh: number | null;
  accuracyMetres: number | null;
  phase: string;
  /** Which driver's leg. */
  leg: number;
  /** True between two readings either side of a lost signal: the line drawn is a guess. */
  silent: boolean;
}

/** A lost signal, as the server draws it: at least 45 s quiet and 250 m apart. */
const SILENT_MS = 45_000;
const SILENT_METRES = 250;

/** Metres between two places, along the ground. */
export function metresBetween(
  a: { longitude: number; latitude: number },
  b: { longitude: number; latitude: number },
): number {
  const radius = 6_371_000;
  const rad = Math.PI / 180;
  const dLat = (b.latitude - a.latitude) * rad;
  const dLon = (b.longitude - a.longitude) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.latitude * rad) * Math.cos(b.latitude * rad) * Math.sin(dLon / 2) ** 2;

  return 2 * radius * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Degrees clockwise from north, from one place to the next. */
export function bearing(
  a: { longitude: number; latitude: number },
  b: { longitude: number; latitude: number },
): number {
  const rad = Math.PI / 180;
  const y = Math.sin((b.longitude - a.longitude) * rad) * Math.cos(b.latitude * rad);
  const x =
    Math.cos(a.latitude * rad) * Math.sin(b.latitude * rad) -
    Math.sin(a.latitude * rad) *
      Math.cos(b.latitude * rad) *
      Math.cos((b.longitude - a.longitude) * rad);

  return (Math.atan2(y, x) / rad + 360) % 360;
}

/**
 * Where the car was at a real moment, or null before anybody had taken the order.
 *
 * After the last reading it stays where it ended, so the end of a replay shows the drop-off
 * rather than an empty map. Between two drivers' legs it is the earlier driver's last place,
 * until the next one's first reading.
 */
export function carAt(legs: readonly JourneyDriverLeg[], real: number): CarAt | null {
  let found: CarAt | null = null;

  for (const [index, leg] of legs.entries()) {
    const points = leg.points;

    if (points.length === 0 || real < Date.parse(points[0].at)) continue;

    const last = points[points.length - 1];

    if (real >= Date.parse(last.at)) {
      found = still(last, index);
      continue;
    }

    // Binary search for the reading at or before the moment.
    let low = 0;
    let high = points.length - 1;

    while (high - low > 1) {
      const mid = (low + high) >> 1;

      if (Date.parse(points[mid].at) <= real) low = mid;
      else high = mid;
    }

    const a = points[low];
    const b = points[high];
    const from = Date.parse(a.at);
    const to = Date.parse(b.at);
    const share = to > from ? (real - from) / (to - from) : 0;
    const apart = metresBetween(a, b);

    found = {
      longitude: a.longitude + (b.longitude - a.longitude) * share,
      latitude: a.latitude + (b.latitude - a.latitude) * share,
      heading: apart > 3 ? bearing(a, b) : (a.headingDegrees ?? null),
      speedKmh: a.speedKmh ?? null,
      accuracyMetres: a.accuracyMetres ?? null,
      phase: a.phase,
      leg: index,
      silent: to - from >= SILENT_MS && apart >= SILENT_METRES,
    };
  }

  return found;
}

function still(point: JourneyPoint, leg: number): CarAt {
  return {
    longitude: point.longitude,
    latitude: point.latitude,
    heading: point.headingDegrees ?? null,
    speedKmh: point.speedKmh ?? null,
    accuracyMetres: point.accuracyMetres ?? null,
    phase: point.phase,
    leg,
    silent: false,
  };
}

/** The readings of every leg cut into runs of one phase, for the map's coloured lines. */
export function phaseRuns(
  legs: readonly JourneyDriverLeg[],
): { phase: string; silent: boolean; coordinates: [number, number][] }[] {
  const runs: { phase: string; silent: boolean; coordinates: [number, number][] }[] = [];

  for (const leg of legs) {
    for (let i = 1; i < leg.points.length; i++) {
      const a = leg.points[i - 1];
      const b = leg.points[i];
      const silent =
        Date.parse(b.at) - Date.parse(a.at) >= SILENT_MS && metresBetween(a, b) >= SILENT_METRES;
      const current = runs[runs.length - 1];

      if (current && current.phase === a.phase && current.silent === silent && i > 1) {
        current.coordinates.push([b.longitude, b.latitude]);
      } else {
        runs.push({
          phase: a.phase,
          silent,
          coordinates: [
            [a.longitude, a.latitude],
            [b.longitude, b.latitude],
          ],
        });
      }
    }
  }

  return runs;
}

// --- Chapters and skipping -----------------------------------------------------

/** The chapter a real moment falls in, by index, or -1 before the first. */
export function chapterAt(chapters: readonly JourneySpan[], real: number): number {
  let found = -1;

  chapters.forEach((chapter, index) => {
    if (real >= Date.parse(chapter.from)) found = index;
  });

  return found;
}

/**
 * Where the skip buttons land: the start of every chapter, and every stop the car is seen to
 * reach on the way, in order.
 */
export function skipTargets(journey: JourneyDetail): number[] {
  const at = [
    ...journey.chapters.map((chapter) => Date.parse(chapter.from)),
    ...journey.events
      .filter((e) => e.kind === 'stopReached' || e.kind === 'destinationReached')
      .map((e) => Date.parse(e.at)),
  ];

  if (journey.finishedAt) at.push(Date.parse(journey.finishedAt));

  return [...new Set(at.filter(Number.isFinite))].sort((a, b) => a - b);
}

/** The next landing after a moment, or null at the end. */
export function nextTarget(targets: readonly number[], real: number): number | null {
  return targets.find((target) => target > real + 500) ?? null;
}

/**
 * The landing before a moment.
 *
 * With a grace of a couple of seconds, like a music player's back button: pressed just after
 * a chapter starts, it goes to the one before rather than to the start of the same one.
 */
export function previousTarget(targets: readonly number[], real: number): number | null {
  const before = targets.filter((target) => target < real - 2000);

  return before.length === 0 ? (targets[0] ?? null) : before[before.length - 1];
}

// --- Speed and keys ------------------------------------------------------------

/** Playback speeds. A ride is twenty minutes; at a hundred and twenty times it is ten seconds. */
export const SPEEDS = [1, 2, 5, 10, 30, 60, 120] as const;

export function faster(speed: number): number {
  return SPEEDS.find((s) => s > speed) ?? SPEEDS[SPEEDS.length - 1];
}

export function slower(speed: number): number {
  return [...SPEEDS].reverse().find((s) => s < speed) ?? SPEEDS[0];
}

/** How far the arrow keys move, in real seconds. */
export const STEP_SECONDS = 10;

export type PlayerAction =
  | 'toggle'
  | 'back'
  | 'forward'
  | 'previousChapter'
  | 'nextChapter'
  | 'slower'
  | 'faster'
  | 'follow'
  | 'start'
  | 'end';

/**
 * What a key does in the player, or null for a key it leaves alone.
 *
 * Never the digits: the console uses 1, 2 and 3 everywhere to switch between its three views,
 * and a replay that took them would strand somebody on the report.
 */
export function actionFor(key: string, shift: boolean): PlayerAction | null {
  switch (key) {
    case ' ':
    case 'k':
      return 'toggle';
    case 'ArrowLeft':
      return shift ? 'previousChapter' : 'back';
    case 'ArrowRight':
      return shift ? 'nextChapter' : 'forward';
    case '-':
    case '_':
      return 'slower';
    case '+':
    case '=':
      return 'faster';
    case 'f':
    case 'F':
      return 'follow';
    case 'Home':
      return 'start';
    case 'End':
      return 'end';
    default:
      return null;
  }
}

/**
 * A distance as a person says it: metres while it is metres, kilometres after — "0 km to the
 * client" says nothing about a car forty metres away.
 */
export function distanceWords(
  t: Translate,
  formats: { distance: (metres: number) => string },
  metres: number,
): string {
  return metres < 1000
    ? t('reports.journeys.metres', { count: Math.round(metres) })
    : formats.distance(metres);
}

// --- The fare ----------------------------------------------------------------

/**
 * The lines of a fare worth printing: one that comes to less than a cent — a rounding that
 * had nothing to round — says nothing but "-0,00".
 */
export function fareLinesToShow<T extends { amount: number }>(lines: readonly T[]): T[] {
  return lines.filter((line) => Math.abs(line.amount) >= 0.005);
}

/**
 * One line of a fare in words, its figures in the firm's own number format.
 *
 * The values arrive as invariant strings — `"3.1"`, `"1.5000"` — because the wire is not where
 * a decimal sign is chosen; as they are, a Bulgarian reads "3.1 км по 1.5000". Only a plain
 * number is reformatted: a surcharge's name stays the name it was given, digits and all.
 */
export function fareLineWords(
  t: Translate,
  line: FareLine,
  decimal: (value: number) => string,
): string {
  const values: Record<string, string> = {};

  for (const [key, raw] of Object.entries(line.arguments)) {
    values[key] = /^-?\d+(\.\d+)?$/.test(raw.trim()) ? decimal(Number(raw)) : raw;
  }

  const words = t(line.code, { ...values, defaultValue: line.code });

  return withTariffName(words, line);
}

/**
 * A line priced at a night or holiday tariff's own prices, with that tariff's name after it.
 *
 * Such a tariff adds no line of its own — its money is the start, the kilometres and the
 * minutes — so its name rides on those lines, in the firm's own words. Said here, it answers
 * the one question a dearer kilometre raises. A surcharge line already reads as its name.
 */
export function withTariffName(words: string, line: FareLine): string {
  const name = line.arguments.name;

  return line.code === 'fare.surcharge' || name === undefined || name.trim() === ''
    ? words
    : `${words} · ${name}`;
}

/** The charges for a ride that never happened: a fee is not a fare. */
const FEES = new Set(['fare.cancellation', 'fare.no_show']);

/** Below this, a ride's kilometres over the best route are the GPS, not the driver. */
const DETOUR_NOISE_METRES = 100;

/** What the fare card says about the money. */
export interface FareFacts {
  /** What the ride came to — or, for one that never happened, the fee — and how it was arrived at. */
  paid: number | null;
  source: 'metered' | 'computed' | 'fee' | null;
  /** What the client was told when the order was taken, and the paid amount less that. */
  quoted: number | null;
  overQuote: number | null;
  /** The tariff's figure, and the taximeter's less it, when the car has a meter that said. */
  computed: number | null;
  meterOverComputed: number | null;
  /** What the client paid for each kilometre driven with them aboard. */
  perKm: number | null;
  /**
   * The kilometres driven over the best route, and what they cost at the tariff's rate per
   * kilometre: nothing when the ride kept to it, null when there is no route or rate to say.
   */
  detourMetres: number | null;
  detourCost: number | null;
}

/** Rounded to the cent, so a difference of a fraction of one reads as none. */
const cents = (amount: number) => Math.round(amount * 100) / 100;

/**
 * The fare, read for a dispute: what it came to, against what was promised, what the
 * meter said, what a kilometre cost, and what the long way round added.
 */
export function fareFacts(journey: JourneyDetail): FareFacts {
  const m = journey.measures;
  const fees = m.fareLines.filter((line) => FEES.has(line.code));
  const fee = fees.length > 0 ? cents(fees.reduce((sum, line) => sum + line.amount, 0)) : null;

  const source =
    m.meteredFare != null
      ? 'metered'
      : m.finalFare != null
        ? 'computed'
        : fee !== null
          ? 'fee'
          : null;
  const paid = m.meteredFare ?? m.finalFare ?? fee;
  const rode = source === 'metered' || source === 'computed';

  const rate = Number(m.fareLines.find((line) => line.code === 'fare.distance')?.arguments.rate);
  const over =
    m.rideDrivenMetres != null && m.rideRecommendedMetres != null
      ? m.rideDrivenMetres - m.rideRecommendedMetres
      : null;

  return {
    paid,
    source,
    quoted: m.estimatedFare,
    overQuote:
      rode && paid !== null && m.estimatedFare != null ? cents(paid - m.estimatedFare) : null,
    computed: m.meteredFare != null ? m.finalFare : null,
    meterOverComputed:
      m.meteredFare != null && m.finalFare != null ? cents(m.meteredFare - m.finalFare) : null,
    perKm:
      rode && paid !== null && m.rideDrivenMetres != null && m.rideDrivenMetres >= 500
        ? paid / (m.rideDrivenMetres / 1000)
        : null,
    detourMetres: over === null ? null : Math.max(0, over),
    detourCost:
      over === null || !Number.isFinite(rate) || rate <= 0
        ? null
        : over >= DETOUR_NOISE_METRES
          ? cents((over / 1000) * rate)
          : 0,
  };
}

// --- What happened -------------------------------------------------------------

/** The parts of the story whose reason is not a reason: a stop's number, the ladder's pick. */
const REASON_UNSAID = new Set(['offered', 'stopReached', 'destinationReached']);

/**
 * What an event's reason adds to its line, in words, or nothing.
 *
 * The line already says what happened — *declined*, *taken over by hand* — so a code adds
 * nothing and is left out rather than printed raw. A driver's reason for turning an offer
 * down, and a passenger's for cancelling, are said in words, as on the order card
 * (`historyReason`); anything else was typed by a person, and is shown as they typed it.
 */
export function eventReason(
  t: Translate,
  kind: string,
  reason: string | null | undefined,
  firmReasons?: Readonly<Record<string, string>>,
): string | null {
  if (!reason || REASON_UNSAID.has(kind)) return null;

  return (
    declineWords(t, reason) ??
    riderCancellationWords(t, reason, firmReasons) ??
    (isCode(reason) ? null : reason)
  );
}

/** The colour of something that happened. */
export function eventTone(kind: string): Tone {
  switch (kind) {
    case 'declined':
    case 'timedOut':
    case 'reassigned':
    case 'needsHuman':
    case 'atRisk':
      return 'warn';
    case 'cancelled':
    case 'noShow':
    case 'withdrawn':
      return 'danger';
    case 'accepted':
    case 'pickedUp':
    case 'streetRide':
    case 'completed':
      return 'ok';
    case 'arrived':
    case 'stopReached':
    case 'destinationReached':
      return 'info';
    default:
      return 'neutral';
  }
}

/**
 * Where in a call's recording a moment falls, in seconds, or null outside it.
 *
 * A recording starts when the call is answered — nobody records the ringing — so a call with
 * no answer has no recording to be in.
 */
export function recordingOffset(call: JourneyCall, real: number): number | null {
  if (!call.hasRecording || !call.answeredAt) return null;

  const start = Date.parse(call.answeredAt);
  const length = (call.recordingSeconds ?? 0) * 1000;
  const into = real - start;

  return into >= 0 && into < length ? into / 1000 : null;
}

/** The fastest speed the audio is played along at; faster than this it is only noise. */
export const AUDIBLE_UP_TO = 2;

/** Seconds past the first moment, for the clock beside the scrubber: `+4:05`. */
export function elapsed(real: number, start: number): string {
  const seconds = Math.max(0, Math.floor((real - start) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, '0');

  return hours > 0
    ? `+${hours}:${String(minutes).padStart(2, '0')}:${rest}`
    : `+${minutes}:${rest}`;
}

// --- The speed panel -----------------------------------------------------------

/** Slower than this, a car between two readings stood still: GPS drifts a few metres. */
const STILL_KMH = 4;

export interface SpeedStats {
  /** The fastest reading, when, and the limit where it was. */
  topKmh: number | null;
  topAt: number | null;
  topLimitKmh: number | null;
  /** Distance over time while moving: a car stuck at lights does not drag it down. */
  averageMovingKmh: number | null;
  /** Seconds spent above the firm's limit where the car was. */
  overLimitSeconds: number;
  /** Seconds standing still, waiting at the pickup included. */
  stillSeconds: number;
}

/**
 * What the speed panel says in figures. Read between consecutive readings, so a silence —
 * two readings far apart in time and place — counts as neither moving nor standing.
 */
export function speedStats(legs: readonly JourneyDriverLeg[]): SpeedStats {
  let topKmh: number | null = null;
  let topAt: number | null = null;
  let topLimitKmh: number | null = null;
  let movingMetres = 0;
  let movingMs = 0;
  let overMs = 0;
  let stillMs = 0;

  for (const leg of legs) {
    leg.points.forEach((point, i) => {
      if (point.speedKmh != null && (topKmh === null || point.speedKmh > topKmh)) {
        topKmh = point.speedKmh;
        topAt = Date.parse(point.at);
        topLimitKmh = point.limitKmh;
      }

      if (i === 0) return;

      const previous = leg.points[i - 1];
      const ms = Date.parse(point.at) - Date.parse(previous.at);
      const metres = metresBetween(previous, point);

      if (ms <= 0 || (ms >= SILENT_MS && metres >= SILENT_METRES)) return;

      const kmh = (metres / ms) * 3600;

      if (kmh < STILL_KMH) {
        stillMs += ms;
      } else {
        movingMetres += metres;
        movingMs += ms;
      }

      if ((previous.speedKmh ?? kmh) > previous.limitKmh) overMs += ms;
    });
  }

  return {
    topKmh,
    topAt,
    topLimitKmh,
    averageMovingKmh: movingMs > 0 ? (movingMetres / movingMs) * 3600 : null,
    overLimitSeconds: Math.round(overMs / 1000),
    stillSeconds: Math.round(stillMs / 1000),
  };
}

/** A stretch of the ride under one limit: the town's, or the open road's. */
export interface LimitRun {
  limitKmh: number;
  /** Real milliseconds of its first and last readings. */
  from: number;
  to: number;
}

/**
 * The limits the ride was driven under, in order: one run for a ride that stayed in town,
 * more for one that left it — so the chart's limit line steps where the car crossed.
 */
export function limitRuns(legs: readonly JourneyDriverLeg[]): LimitRun[] {
  const runs: LimitRun[] = [];

  for (const point of legs.flatMap((leg) => leg.points)) {
    const at = Date.parse(point.at);
    const last = runs[runs.length - 1];

    if (last !== undefined && last.limitKmh === point.limitKmh) last.to = at;
    else runs.push({ limitKmh: point.limitKmh, from: at, to: at });
  }

  return runs;
}

/** The different limits the ride was driven under, lowest first. */
export function rideLimits(legs: readonly JourneyDriverLeg[]): number[] {
  return [...new Set(legs.flatMap((leg) => leg.points.map((p) => p.limitKmh)))].sort(
    (a, b) => a - b,
  );
}

/**
 * The speed axis: a round top above the fastest reading and the limit, and three or four
 * round steps up to it — `0 · 50 · 100 · 150`, never `0 · 37 · 74`.
 */
export function speedTicks(topKmh: number | null, limitKmh: number): number[] {
  const top = Math.max(topKmh ?? 0, limitKmh * 1.1, 20);
  const step = [10, 20, 25, 50, 100].find((s) => Math.ceil(top / s) <= 4) ?? 100;
  const count = Math.ceil(top / step);

  return Array.from({ length: count + 1 }, (_, i) => i * step);
}

// --- The order panel -----------------------------------------------------------

/** How long each chapter lasted, and how far the car went in it. */
export interface ChapterSpan {
  kind: string;
  from: number;
  seconds: number;
  metres: number | null;
}

/** The order's time, part by part, for the bar on the order panel. */
export function chapterSpans(journey: JourneyDetail): ChapterSpan[] {
  const metres: Record<string, number> = {};

  for (const leg of journey.legs) {
    for (let i = 1; i < leg.points.length; i++) {
      const a = leg.points[i - 1];
      const b = leg.points[i];

      metres[a.phase] = (metres[a.phase] ?? 0) + metresBetween(a, b);
    }
  }

  return journey.chapters.map((chapter) => {
    const from = Date.parse(chapter.from);

    return {
      kind: chapter.kind,
      from,
      seconds: Math.max(0, Math.round((Date.parse(chapter.to) - from) / 1000)),
      // Waiting is standing at the door: whatever the readings wander there is not distance.
      metres:
        MOVING.has(chapter.kind) && journey.legs.length > 0 ? (metres[chapter.kind] ?? 0) : null,
    };
  });
}

/** One thing wrong with an order, with the moment to show and the figures to say it with. */
export interface ProblemItem {
  problem: Problem;
  /** Real milliseconds to jump to, or null when there is no moment to show. */
  at: number | null;
  seconds?: number;
  kmh?: number;
  limitKmh?: number;
  metres?: number;
  recommendedMetres?: number;
  percent?: number;
}

/**
 * Every problem of an order, spelled out: one line per late arrival, per stop, per stretch
 * of speeding, per silence — each with the moment it happened, in order.
 *
 * Built from what the server already judged by the firm's own thresholds, so the panel
 * cannot flag something the list did not, or the other way round.
 */
export function problemItems(journey: JourneyDetail): ProblemItem[] {
  const items: ProblemItem[] = [];
  const has = (problem: Problem) => journey.problems.includes(problem);
  const m = journey.measures;
  const aboard = journey.chapters.find((c) => c.kind === 'aboard');

  if (has('late') && m.lateSeconds != null) {
    const at = m.arrivedAt ?? journey.finishedAt;
    items.push({ problem: 'late', at: at ? Date.parse(at) : null, seconds: m.lateSeconds });
  }

  if (has('detour')) {
    items.push({
      problem: 'detour',
      at: aboard ? Date.parse(aboard.from) : null,
      metres: m.rideDrivenMetres ?? undefined,
      recommendedMetres: m.rideRecommendedMetres ?? undefined,
      percent: m.detourPercent ?? undefined,
    });
  }

  if (has('longWait') && m.arrivedAt && m.waitSeconds != null) {
    items.push({ problem: 'longWait', at: Date.parse(m.arrivedAt), seconds: m.waitSeconds });
  }

  const spanItems = (kind: string, problem: Problem) =>
    journey.spans
      .filter((s) => s.kind === kind && s.flagged)
      .forEach((s) =>
        items.push({
          problem,
          at: Date.parse(s.from),
          seconds: Math.round((Date.parse(s.to) - Date.parse(s.from)) / 1000),
          kmh: s.peakKmh ?? undefined,
          limitKmh: s.limitKmh ?? undefined,
        }),
      );

  if (has('longStop')) spanItems('stop', 'longStop');
  if (has('speeding')) spanItems('speeding', 'speeding');
  if (has('signalLost')) spanItems('silence', 'signalLost');

  if (has('cancelledOnTheWay') && journey.finishedAt) {
    const at = Date.parse(journey.finishedAt);
    const car = carAt(journey.legs, at);

    items.push({
      problem: 'cancelledOnTheWay',
      at,
      metres: car === null ? undefined : metresBetween(car, journey.pickup),
    });
  }

  if (has('noTrack')) items.push({ problem: 'noTrack', at: null });

  return items.sort((a, b) => (a.at ?? Infinity) - (b.at ?? Infinity));
}
